import cookieCommons from 'common/cookieCommons';
import { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTus } from 'use-tus';
import { uploadFilesEndpoint } from '../apis/tms/upload.api';
import { generateUUIDUsingMathRandom } from '../common/generateGUID';
import { hashAccpetTypeUpload } from '../constant/fileType';
import getVideoDuration from '../helpers/getVideoDuration';
import { IUseTusInvidualUpload } from '../interfaces/hook.interface';
import {
  FileUploadStatus,
  FileUploadStatusType,
} from '../interfaces/tusFileUpload.interface';

const chunkParallel = 3;

const useTusInvidualUpload = ({
  idSaveOn = undefined,
  onSuccess,
  onError,
}: IUseTusInvidualUpload) => {
  const [fileList, setFileList] = useState<
    { file: File; status: FileUploadStatusType }[]
  >([]);
  const [progress, setProgress] = useState<number>(0);
  const [currentFileIndex, setCurrentFileIndex] = useState<number>(NaN);

  const { setUpload, ...props } = useTus({
    autoStart: true,
    uploadOptions: {
      endpoint: uploadFilesEndpoint,
      parallelUploads: chunkParallel,
      headers: {
        Authorization: `Bearer ${cookieCommons.getAccessToken()}`,
      },
    },
  });

  const abortUpload = useCallback(() => {
    props.upload?.abort();
  }, [props]);

  const continueUpload = useCallback(async () => {
    const latestPreviosUpload = (
      await props.upload?.findPreviousUploads()
    )?.pop();

    return (
      latestPreviosUpload &&
      props.upload?.resumeFromPreviousUpload(latestPreviosUpload)
    );
  }, [props]);

  const handleSetFiles = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const fileList = Array.from(event.target.files || []);
      if (fileList.length <= 0) {
        return;
      }
      setFileList(() =>
        fileList.map((item) => ({
          file: item,
          status: FileUploadStatus.Pending,
        }))
      );
      setCurrentFileIndex(0);
    },
    []
  );

  const setErrorStatus = useCallback((currentFileIndex) => {
    setFileList((oldState) =>
      oldState.map((item, _index) =>
        _index === currentFileIndex
          ? { file: item.file, status: FileUploadStatus.Error }
          : item
      )
    );
    setCurrentFileIndex((oldIndex) => oldIndex + 1);
  }, []);

  const setSuccessStatus = useCallback((currentFileIndex) => {
    setFileList((oldState) =>
      oldState.map((item, _index) =>
        _index === currentFileIndex
          ? { file: item.file, status: FileUploadStatus.Success }
          : item
      )
    );
    setCurrentFileIndex((oldIndex) => oldIndex + 1);
  }, []);

  useEffect(() => {
    (async () => {
      if (fileList.length > 0 && currentFileIndex < fileList.length) {
        const currentFile = fileList[currentFileIndex].file;

        const internal_user_id = cookieCommons.getTmsUserId() ?? '';

        let duration: number | undefined = undefined;
        if (
          hashAccpetTypeUpload[
            currentFile.type as keyof typeof hashAccpetTypeUpload
          ].includes('video/')
        ) {
          duration = await getVideoDuration(currentFile);
        }
        const UUID = generateUUIDUsingMathRandom();
        const preMetadata = {
          name: currentFile.name.normalize('NFC'),
          internal_user_id,
          file_type:
            hashAccpetTypeUpload[
              currentFile.type as keyof typeof hashAccpetTypeUpload
            ],
          file_size: `${currentFile.size}`,
          file_id: UUID,
        };

        const generateMetadata = (propArray: { [key: string]: any }[]) => {
          let clone = preMetadata;
          propArray.forEach((item) => {
            for (const key in item) {
              if (item[key]) {
                clone = { ...clone, [key]: item[key] };
              }
            }
          });
          return clone;
        };

        setUpload(currentFile, {
          chunkSize: currentFile.size / 10,
          metadata: generateMetadata([
            { video_duration: duration },
            { folder_id: idSaveOn },
          ]),
          onProgress(bytesUpload: number, bytesTotal: number) {
            setProgress(+((bytesUpload / bytesTotal) * 100));
          },
          onSuccess() {
            ReactDOM.unstable_batchedUpdates(() => {
              props.remove();
              setSuccessStatus(currentFileIndex);
              onSuccess && onSuccess(UUID);
            });
          },
          onError(_error: any) {
            ReactDOM.unstable_batchedUpdates(() => {
              props.remove();
              setErrorStatus(currentFileIndex);
              onError && onError(UUID);
            });
          },
        });
      }
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFileIndex, fileList]);

  return {
    handleSetFiles,
    progress,
    fileList,
    currentFileIndex,
    abortUpload,
    continueUpload,
    ...props,
  };
};

//cmt lại vì đang trong quá trình tìm hiểu, còn trong doccen v1 chỉ upload single file nên cmt lại để tập trung làm phần kia cho tốt

// const useTusParallelUpload = ({
//   _numberOfParallel = 3, //default number of file is uploaded parallel
// }: {
//   _numberOfParallel?: number;
// }) => {
//   const [fileList, setFileList] = useState<
//     { file: File; status: FileUploadStatusType }[]
//   >([]);
//   const [progress, setProgress] = useState<number[]>(
//     new Array(_numberOfParallel).fill(0)
//   );
//   const [currentFileIndexList, setCurrentFileIndexList] = useState<number[]>(
//     []
//   );

//   const { setUpload, ...props } = useTus({
//     autoStart: true,
//     uploadOptions: {
//       endpoint: uploadFilesEndpoint(),
//       parallelUploads: chunkParallel,
//       headers: {
//         Authorization: `Bearer ${Cookies.get(EAuthCookiesKey.Token)}`,
//       },
//     },
//   });

//   const abortUpload = useCallback(() => {
//     props.upload?.abort();
//   }, [props]);

//   const continueUpload = useCallback(async () => {
//     const latestPreviosUpload = (
//       await props.upload?.findPreviousUploads()
//     )?.pop();

//     return (
//       latestPreviosUpload &&
//       props.upload?.resumeFromPreviousUpload(latestPreviosUpload)
//     );
//   }, [props]);

//   const handleSetFiles = useCallback(
//     (event: React.ChangeEvent<HTMLInputElement>) => {
//       const fileList = Array.from(event.target.files || []);
//       if (fileList.length <= 0) {
//         return;
//       }
//       setFileList(() =>
//         fileList.map((item) => ({
//           file: item,
//           status: FileUploadStatus.Pending,
//         }))
//       );
//       setCurrentFileIndexList([0, 1, 2]); //first 3 files
//     },
//     []
//   );
// };

export { useTusInvidualUpload };
